import {
  ApiErrorSchema,
  SessionResponseSchema,
  SummaryResponseSchema,
  type ApiErrorCode,
  type SessionRequest,
  type SessionResponse,
  type SummaryRequest,
} from '../shared/demo-api';
import type { SummaryView } from '../shared/summary-view';

export class DemoApiError extends Error {
  override name = 'DemoApiError';
  constructor(readonly code: ApiErrorCode | 'network') {
    super(code);
  }
}

async function post(path: string, body: SessionRequest | SummaryRequest): Promise<Response> {
  try {
    return await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new DemoApiError('network');
  }
}

async function failure(response: Response): Promise<DemoApiError> {
  const parsed = ApiErrorSchema.safeParse(await response.json().catch(() => null));
  return new DemoApiError(parsed.success ? parsed.data.error : 'upstream');
}

export async function requestSession(turnstileToken: string): Promise<SessionResponse> {
  const response = await post('/api/session', { turnstileToken });
  if (!response.ok) throw await failure(response);
  return SessionResponseSchema.parse(await response.json());
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Polls until the provider has processed the conversation (it takes a few seconds). */
export async function waitForSummary(
  request: SummaryRequest,
  { intervalMs = 2000, timeoutMs = 60_000 } = {},
): Promise<SummaryView> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const response = await post('/api/summary', request);
    if (!response.ok && response.status !== 202) throw await failure(response);
    const reply = SummaryResponseSchema.parse(await response.json());
    if (reply.status === 'done') return reply.view;
    if (Date.now() > deadline) throw new DemoApiError('upstream');
    await sleep(intervalMs);
  }
}
